import { useEffect } from 'react';
import { onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { useAuthStore } from '@/stores/authStore';
import { fetchWithAuth } from '@/lib/api';

/**
 * Firebase 인증 상태를 Zustand 스토어와 동기화하는 컴포넌트
 * - 앱 시작 시 로그인 상태 자동 복원
 * - 토큰 만료 시 자동 갱신
 * - 탭 전환/복귀 시에도 인증 유지
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { login, logout, setLoading, setPoints, setCoins } = useAuthStore();

  useEffect(() => {
    setLoading(true);

    // Google Redirect 결과 처리 (signInWithRedirect 폴백 시)
    getRedirectResult(auth).then(async (result) => {
      if (result?.user) {
        // 신규/기존 유저 처리
        try {
          const meRes = await fetchWithAuth('/api/auth/me');
          if (!meRes.ok) {
            const username = result.user.displayName || (result.user.email || '').split('@')[0] || 'User';
            await fetchWithAuth('/api/auth/register', {
              method: 'POST',
              body: JSON.stringify({ username }),
            });
          }
        } catch { /* onAuthStateChanged에서 처리 */ }
      }
    }).catch(() => { /* redirect 없으면 무시 */ });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Firestore에서 역할 조회
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const role = userDoc.exists()
            ? (userDoc.data().role as 'user' | 'aspirant' | 'author' | 'admin' | 'super-admin')
            : 'user';

          login(
            user.uid,
            user.email || '',
            user.displayName || '',
            role
          );
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setPoints(userData.points || 0);
            setCoins(userData.paidCoins || 0, userData.freeCoins || 0);
          }
        } catch {
          // Firestore 조회 실패해도 기본 역할로 로그인 유지
          login(user.uid, user.email || '', user.displayName || '', 'user');
        }
      } else {
        logout();
      }
    });

    // 토큰 자동 갱신 (50분마다, 만료 전 갱신)
    const tokenRefreshInterval = setInterval(async () => {
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch {
          // 갱신 실패 시 무시 (다음 API 호출에서 재시도)
        }
      }
    }, 50 * 60 * 1000);

    return () => {
      unsubscribe();
      clearInterval(tokenRefreshInterval);
    };
  }, [login, logout, setLoading, setPoints, setCoins]);

  return <>{children}</>;
}
